import { axiosInstance } from "./index.ts";
import { User, type UserAddress } from "./user.ts";
import { TokenStorage } from "./tokenStorage";
import { publishAuthEvent } from "@/shared/lib/authChannel";
import { emitSessionExpired } from "@/shared/lib/sessionEvents";
import { sessionHint } from "@/shared/lib/sessionHint";
import { getHttpStatus } from "@/shared/lib/httpStatus";

// Types for authentication responses

export interface LoginResponse {
	user: User;
	accessToken: string;
	/** Refresh-token expiry (ISO). Absent from backends that predate it. */
	sessionExpiresAt?: string;
	reset_password_required?: boolean;
}

export interface LoginRequest {
	email: string;
	password: string;
	rememberMe?: boolean;
}

export interface OTPLoginRequest {
	email: string;
	otp: string;
}

export interface GenerateOTPRequest {
	email: string;
}

export interface GoogleSignupExchangeResponse {
	signupToken: string;
	profile: { email: string };
	expiresIn: number;
}

export const authenticationService = {
	// Regular login with email and password
	login: async (credentials: LoginRequest): Promise<LoginResponse> => {
		const response = await axiosInstance.post("/users/login", credentials);
		const data = response.data;

		// Store access token in memory (refresh token is set as HttpOnly cookie by backend)
		TokenStorage.setAccessToken(data.accessToken);
		TokenStorage.setSessionExpiresAt(data.sessionExpiresAt);

		return data;
	},

	// Organization login with email and password
	organizationLogin: async (credentials: LoginRequest): Promise<LoginResponse> => {
		const response = await axiosInstance.post("/organization/login", credentials);
		const data = response.data;

		// Store access token in memory (refresh token is set as HttpOnly cookie by backend)
		TokenStorage.setAccessToken(data.accessToken);
		TokenStorage.setSessionExpiresAt(data.sessionExpiresAt);

		return data;
	},

	// Login with OTP
	loginWithOTP: async (credentials: OTPLoginRequest): Promise<LoginResponse> => {
		const response = await axiosInstance.post("/users/login-with-otp", credentials);
		const data = response.data;

		// Store access token in memory (refresh token is set as HttpOnly cookie by backend)
		TokenStorage.setAccessToken(data.accessToken);
		TokenStorage.setSessionExpiresAt(data.sessionExpiresAt);

		return data;
	},

	// Organization login with OTP
	organizationLoginWithOTP: async (credentials: OTPLoginRequest): Promise<LoginResponse> => {
		const response = await axiosInstance.post("/organization/login-with-otp", credentials);
		const data = response.data;

		// Store access token in memory (refresh token is set as HttpOnly cookie by backend)
		TokenStorage.setAccessToken(data.accessToken);
		TokenStorage.setSessionExpiresAt(data.sessionExpiresAt);

		return data;
	},

	// Google OAuth login
	googleLogin: async (data: { code: string; redirectUri: string }): Promise<LoginResponse> => {
		const response = await axiosInstance.post("/users/google-login", data);
		const responseData = response.data;
		TokenStorage.setAccessToken(responseData.accessToken);
		TokenStorage.setSessionExpiresAt(responseData.sessionExpiresAt);
		return responseData;
	},

	exchangeGoogleSignup: async (data: {
		code: string;
		redirectUri: string;
		registrationRole: "creator" | "client" | "agency";
	}): Promise<GoogleSignupExchangeResponse> => {
		const response = await axiosInstance.post("/users/google-signup/exchange", data);
		return response.data;
	},

	completeGoogleSignup: async (data: {
		signupToken: string;
		firstName?: string;
		lastName?: string;
		tosAccepted: boolean;
		tosVersion: string;
		timeZone?: string;
	}): Promise<{ message: string; user: User; accessToken: string }> => {
		const response = await axiosInstance.post("/users/google-signup/complete", data);
		TokenStorage.setAccessToken(response.data.accessToken);
		TokenStorage.setSessionExpiresAt(response.data.sessionExpiresAt);
		return response.data;
	},

	// Generate OTP for login
	generateLoginOTP: async (email: GenerateOTPRequest): Promise<{ message: string }> => {
		const response = await axiosInstance.post("/users/generate-login-otp", email);
		return response.data;
	},

	// Generate OTP for organization login
	generateOrganizationLoginOTP: async (email: GenerateOTPRequest): Promise<{ message: string }> => {
		// If BE exposes an org-specific generate endpoint, adjust here; else reuse users endpoint
		const response = await axiosInstance.post("/users/generate-login-otp", email);
		return response.data;
	},

	// Organization reset password
	organizationResetPassword: async (data: { email: string; newPassword: string }): Promise<LoginResponse> => {
		const response = await axiosInstance.post("/organization/reset-password", data);
		const responseData = response.data;

		// Store access token in memory (refresh token is set as HttpOnly cookie by backend)
		TokenStorage.setAccessToken(responseData.accessToken);
		TokenStorage.setSessionExpiresAt(responseData.sessionExpiresAt);

		return responseData;
	},

	// Update user profile
	updateUser: async (data: {
		name: string;
		legalFirstName?: string;
		legalLastName?: string;
		mobileCountryCode?: string;
		mobileNumber?: string;
		profileImage?: string;
		address?: UserAddress;
	}): Promise<User> => {
		const response = await axiosInstance.put("/users", data);
		return response.data;
	},

	// Get current access token
	getAccessToken: (): string | null => {
		return TokenStorage.getAccessToken();
	},

	// Logout - revoke refresh token on server and clear all tokens locally
	logout: async (): Promise<void> => {
		try {
			await axiosInstance.post("/auth/revoke");
		} catch (e) {
			// Ignore errors; still clear client tokens
		} finally {
			TokenStorage.clearAccessToken();
			sessionStorage.removeItem("userRoles");
			sessionStorage.removeItem("campaignRoles");
			// Notify other tabs that the user logged out
			publishAuthEvent("logout");
		}
	},

	// Clear local tokens only (no server call, no cross-tab broadcast).
	// Used by the remote auth event handler to avoid rebroadcast loops.
	clearLocalTokens: (): void => {
		TokenStorage.clearAccessToken();
		sessionStorage.removeItem("userRoles");
		sessionStorage.removeItem("campaignRoles");
	},

	// Set access token (for token refresh)
	setAccessToken: (token: string): void => {
		TokenStorage.setAccessToken(token);
	},

	// Refresh access token using refresh token
	refreshAccessToken: async (): Promise<{ accessToken: string; user: User; sessionExpiresAt?: string }> => {
		// Add timeout to prevent hanging requests
		const timeoutPromise = new Promise<never>((_, reject) => {
			setTimeout(() => reject(new Error("Refresh request timeout")), 10000); // 10 second timeout
		});

		const refreshPromise = axiosInstance.post<{ accessToken: string; user: User; sessionExpiresAt?: string }>(
			"/auth/refresh",
		);

		try {
			const response = await Promise.race([refreshPromise, timeoutPromise]);
			const { accessToken, user, sessionExpiresAt } = response.data;

			// Store the new access token
			TokenStorage.setAccessToken(accessToken);
			TokenStorage.setSessionExpiresAt(sessionExpiresAt);

			return { accessToken, user, sessionExpiresAt };
		} catch (error) {
			// Only a 401 means the session is gone (the backend owns and clears
			// invalid HttpOnly refresh cookies). On a timeout, network error or
			// 5xx the current access token is kept: it may still be valid, and
			// the interceptor can only refresh on demand while a token exists.
			if (getHttpStatus(error) === 401) {
				TokenStorage.clearAccessToken();
				sessionHint.clear();
				emitSessionExpired("refresh-rejected");
			}
			throw error;
		}
	},
};
